import React, { useState, useEffect } from 'react';
import { Row, Col, Tag, message, notification } from 'antd';
import { updateExportStatus, generateExportInvoice, storeExportComment, showExport } from '@app/api/export.api';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import RejectPopup from '../common/RejectPopup';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import CancelPopup from '../common/CancelPopup';

interface ExportWorkflowButtonsProps {
  status: string | null;
  id: string | number | null;
  setStatus: React.Dispatch<React.SetStateAction<string | null>>;
  setWfActions: React.Dispatch<React.SetStateAction<string[]>>;
  wfActions: string[];
  currentType: 'NORMAL' | 'DRUG';
  onViewInvoicesClick: any;
}

const NORMAL_EXPORT_ACTIONS: Record<string, string> = {
  DRAFT_TO_PENDING_DOCUMENT: 'Create Task', // Step 1
  PENDING_DOCUMENT_TO_PENDING_VERIFICATION_AQO: 'Submit Documents', // Step 2
  PENDING_VERIFICATION_AQO_TO_REJECTED: 'Reject',
  PENDING_VERIFICATION_AQO_TO_PENDING_DIRECTOR_APPROVAL: 'Submit for DA',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_DG_APPROVAL: 'Submit for DG Approval',
  PENDING_DIRECTOR_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_VERIFICATION_AQO: 'Send Back to AQO',
  PENDING_DG_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_DG_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL: 'Send Back to DA',
  PENDING_DG_APPROVAL_TO_PENDING_VERIFICATION_AQO: 'Send Back to AQO',
  PENDING_VERIFICATION_AQO_TO_PENDING_CUSTOMER_VISIT: 'Schedule Customer Visit',
  PENDING_CUSTOMER_VISIT_TO_PENDING_PAYMENT: 'Proceed to Payment',
  PENDING_CUSTOMER_VISIT_TO_REJECTED: 'Reject',
  PENDING_PAYMENT_TO_PENDING_CERTIFICATE_APPROVAL: 'Submit for Certificate Approval',
  PENDING_CERTIFICATE_APPROVAL_TO_PENDING_LDO_APPROVAL: 'Submit for LDO Approval',
  PENDING_CERTIFICATE_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_CERTIFICATE_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL: 'Submit for DA',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_CERTIFICATE_APPROVAL: 'Send Back to Certificate Approval',
  PENDING_DG_APPROVAL_TO_PENDING_CERTIFICATE_APPROVAL: 'Send Back to Certificate Approval',
  PENDING_LDO_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_LDO_APPROVAL_TO_PENDING_CUSTOMS: 'Submit for Customs Clearance',
  PENDING_CUSTOMS_TO_COMPLETE: 'Complete Process',
  PENDING_CUSTOMS_TO_REJECTED: 'Reject',
};

const DRUG_EXPORT_ACTIONS: Record<string, string> = {
  DRAFT_TO_PENDING_DOCUMENT: 'Create Task',
  PENDING_DOCUMENT_TO_PENDING_VERIFICATION_AQO: 'Submit Documents',
  PENDING_VERIFICATION_AQO_TO_REJECTED: 'Reject',
  PENDING_VERIFICATION_AQO_TO_PENDING_DIRECTOR_APPROVAL: 'Submit for DA',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_DG_APPROVAL: 'Submit for DG Approval',
  PENDING_DIRECTOR_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_VERIFICATION_AQO: 'Send Back to AQO',
  PENDING_DG_APPROVAL_TO_REJECTED: 'Reject',
  PENDING_DG_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL: 'Send Back to DA',
  PENDING_DG_APPROVAL_TO_PENDING_VERIFICATION_AQO: 'Send Back to AQO',
  PENDING_VERIFICATION_AQO_TO_PENDING_PAYMENT: 'Proceed to Payment',
  PENDING_PAYMENT_TO_PENDING_DO_APPROVAL: 'Submit for DO Approval',
  PENDING_DO_APPROVAL_TO_PENDING_REGISTRAR_APPROVAL: 'Submit for Registrar Approval',
  PENDING_REGISTRAR_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL: 'Submit for DA Approval',
  PENDING_DIRECTOR_APPROVAL_TO_PENDING_REGISTRAR_APPROVAL: 'Send Back to Registrar Approval',
  PENDING_DG_APPROVAL_TO_PENDING_REGISTRAR_APPROVAL: 'Send Back to Registrar Approval',
  PENDING_REGISTRAR_APPROVAL_TO_PENDING_SIGNED_CERTIFICATE: 'Proceed to Certificate Signing',
  PENDING_SIGNED_CERTIFICATE_TO_PENDING_CUSTOMS: 'Submit for Customs Clearance',
  PENDING_CUSTOMS_TO_COMPLETE: 'Complete Process',
  PENDING_CUSTOMS_TO_REJECTED: 'Reject',
};

const PopupActions = [
  'PENDING_VERIFICATION_AQO_TO_REJECTED',
  'PENDING_VERIFICATION_AQO_TO_PENDING_DIRECTOR_APPROVAL',
  'PENDING_DIRECTOR_APPROVAL_TO_PENDING_DG_APPROVAL',
  'PENDING_DIRECTOR_APPROVAL_TO_REJECTED',
  'PENDING_DIRECTOR_APPROVAL_TO_PENDING_VERIFICATION_AQO',
  'PENDING_DG_APPROVAL_TO_PENDING_VERIFICATION_AQO',
  'PENDING_DG_APPROVAL_TO_REJECTED',
  'PENDING_DG_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL',
  'PENDING_CUSTOMER_VISIT_TO_REJECTED',
  'PENDING_REGISTRAR_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL',
  'PENDING_CERTIFICATE_APPROVAL_TO_REJECTED',
  'PENDING_CERTIFICATE_APPROVAL_TO_PENDING_DIRECTOR_APPROVAL',
  'PENDING_DIRECTOR_APPROVAL_TO_PENDING_REGISTRAR_APPROVAL',
  'PENDING_DG_APPROVAL_TO_PENDING_REGISTRAR_APPROVAL',
  'PENDING_LDO_APPROVAL_TO_REJECTED',
  'PENDING_CUSTOMS_TO_REJECTED',
];

const PaymentActions = ['PENDING_PAYMENT_TO_PENDING_CERTIFICATE_APPROVAL', 'PENDING_PAYMENT_TO_PENDING_DO_APPROVAL'];

const ExportWorkflowButtons: React.FC<ExportWorkflowButtonsProps> = ({
  status,
  id,
  setStatus,
  setWfActions,
  wfActions,
  currentType,
  onViewInvoicesClick,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [localWfActions, setLocalWfActions] = useState<string[]>(wfActions || []);
  const handleErrorMessages = useApiErrorHandler();
  const [popupTitle, setPopupTitle] = useState('Reject');

  // Keep localWfActions in sync if wfActions prop changes
  useEffect(() => {
    setLocalWfActions(wfActions || []);
  }, [wfActions]);

  if (!id) return null;

  const actionLabels = currentType === 'DRUG' ? DRUG_EXPORT_ACTIONS : NORMAL_EXPORT_ACTIONS;

  const handleWorkflowAction = async (action: string) => {
    // if (action === 'PENDING_VERIFICATION_AQO_TO_PENDING_CUSTOMER_VISIT') {
    //   const isDatesSet = true; // Replace with real logic if needed
    //   if (!isDatesSet) {
    //     notification.error({
    //       message: 'Cannot Proceed',
    //       description: 'Please set required dates before continuing.',
    //     });
    //     return;
    //   }
    // }

    await handleStatusChange(action);

    if (
      action === 'PENDING_VERIFICATION_AQO_TO_PENDING_PAYMENT' ||
      action === 'PENDING_CUSTOMER_VISIT_TO_PENDING_PAYMENT'
    ) {
      try {
        await generateExportInvoice(Number(id), 'EXPORT');
      } catch (error) {
        console.error('Error generating export invoice:', error);
        message.error('Failed to generate export invoice.');
        return;
      }
    }
  };

  const handleStatusChange = async (
    action: string,
    extraData?: {
      comment_for?: string;
      msg?: string;
    },
  ) => {
    try {
      await updateExportStatus(Number(id), { action, ...extraData });
      const exportData = await showExport(Number(id));
      const newStatus = exportData?.export?.status;
      const newWfActions = exportData?.wf_actions || [];

      if (newStatus) {
        setStatus(newStatus);
        setWfActions(newWfActions);
        setLocalWfActions(newWfActions);
        message.success(`Export status updated to ${newStatus}.`);
      } else {
        message.warning('Export status updated but response did not include status.');
      }
    } catch (err: any) {
      handleErrorMessages(err);
      console.error('Error updating export status:', err);
    }
  };

  const handleRejectSubmit = async (values: { commentFor: string; comment: string }) => {
    if (!pendingAction) return;

    setIsSubmitting(true);

    try {
      await handleStatusChange(pendingAction, {
        comment_for: values.commentFor,
        msg: values.comment,
      });

      setRejectModalVisible(false);
      setPendingAction(null);
    } catch (error) {
      console.error('Error submitting rejection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        style={{
          padding: '12px 16px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
          minHeight: '64px',
        }}
      >
        <Col flex="auto">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {/* {status !== 'COMPLETE' && status !== 'DONE' && status !== 'REJECTED' && localWfActions.length > 0
            ? localWfActions.map((action) => {
                const label = actionLabels[action];
                if (!label) return null;

                return (
                  <BaseButton
                    key={action}
                    type="primary"
                    size="small"
                    onClick={() => handleWorkflowAction(action)}
                    disabled={isSubmitting}
                  >
                    {label}
                  </BaseButton>
                );
              })
            : null} */}

            {wfActions.map((action) => {
              if (PaymentActions.includes(action)) return null;

              return (
                <BaseButton
                  key={action}
                  type="primary"
                  size="small"
                  onClick={() => {
                    if (PopupActions.includes(action)) {
                      setPendingAction(action);
                      setPopupTitle(actionLabels[action] || 'Action');
                      setRejectModalVisible(true);
                      return;
                    }

                    handleWorkflowAction(action);
                  }}
                >
                  {actionLabels[action] || 'Next Step'}
                </BaseButton>
              );
            })}

            {wfActions.some((action) => PaymentActions.includes(action)) && (
              <BaseButton type="primary" size="small" onClick={onViewInvoicesClick}>
                View Invoices
              </BaseButton>
            )}
          </div>
          <RejectPopup
            key={pendingAction || 'default'}
            title={popupTitle}
            visible={isRejectModalVisible}
            onClose={() => {
              setRejectModalVisible(false);
              setPendingAction(null);
            }}
            onSubmit={handleRejectSubmit}
            isLoading={isSubmitting}
          />
        </Col>
        <Col>
          <span style={{ fontWeight: 'bold', marginRight: 5 }}>Status:</span>
          <Tag
            color={status === 'COMPLETE' || status === 'DONE' ? 'green' : status === 'REJECTED' ? 'red' : 'orange'}
            style={{ fontSize: 14, padding: '4px 10px', borderRadius: 6 }}
          >
            {status || 'N/A'}
          </Tag>
        </Col>
      </Row>
    </>
  );
};

export default ExportWorkflowButtons;
